import React, { useEffect, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Text from "@amzn/meridian/text";
import { Divider, Icon, Tag } from "@amzn/meridian";
import { TagType } from "@amzn/meridian/tag/tag";
import chevronRightSmallTokens from "@amzn/meridian-tokens/base/icon/chevron-right-small";
import clipboardTokens from "@amzn/meridian-tokens/base/icon/clipboard";
import { useNavigate } from "react-router-dom";
import austinInspectionService, {
  InspectionUrgency,
  ScheduledInspection,
} from "src/services/austinInspectionService";
import { formatDateTimeWithZone } from "src/utils/dateUtil";
import "./landing.scss";
import { AustinInspectionDetailsState } from "../configurationDrivenInspections/austinInspectionCheckpointsPage";

// Maps an inspection's urgency to a Meridian Tag color type.
const URGENCY_TAG_TYPES: Record<InspectionUrgency, TagType> = {
  DUE: "warning",
  UPCOMING: "warning",
};

// Human-readable labels for the urgency pill.
const URGENCY_LABELS: Record<InspectionUrgency, string> = {
  DUE: "Due",
  UPCOMING: "Upcoming",
};

const DESCRIPTION = "AI-guided visual compliance check";

const getUrgencyTagType = (urgency: InspectionUrgency): TagType =>
  URGENCY_TAG_TYPES[urgency] ?? "neutral";

const getUrgencyLabel = (urgency: InspectionUrgency): string =>
  URGENCY_LABELS[urgency] ?? urgency;

/**
 * Builds the secondary date line shown under the title:
 *   - DUE:      "Due: <dueDate>"
 *   - UPCOMING: "Next: <scheduledDateTime>"
 */
const buildDateLabel = (inspection: ScheduledInspection): string => {
  if (inspection.urgency === "DUE" && inspection.dueDate) {
    return `Due: ${formatDateTimeWithZone(inspection.dueDate)}`;
  }
  if (inspection.scheduledDateTime) {
    return `Next: ${formatDateTimeWithZone(inspection.scheduledDateTime)}`;
  }
  return "";
};

interface AustinInspectionCardProps {
  /** Site code selected in the toggle (e.g. "DEMO1"). */
  siteCode: string;
}

const AustinInspectionCard: React.FC<AustinInspectionCardProps> = ({ siteCode }) => {
  const navigate = useNavigate();
  const [inspection, setInspection] = useState<ScheduledInspection | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchInspection = async () => {
      if (!siteCode) {
        setIsLoading(false);
        return;
      }
      setIsLoading(true);
      setError(null);
      try {
        const result = await austinInspectionService.getCurrentScheduledInspection(siteCode);
        if (!cancelled) setInspection(result);
      } catch (e: any) {
        if (!cancelled) setError(e?.message || "Failed to load inspection");
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    fetchInspection();
    return () => {
      cancelled = true;
    };
  }, [siteCode]);

  if (isLoading) {
    return (
      <div className="home-card">
        <Row spacingInset="400">
          <Text type="b200" color="secondary">Loading inspection…</Text>
        </Row>
      </div>
    );
  }

  // Nothing DUE or UPCOMING for this site, or the fetch failed — render nothing
  // so the workflow section stays clean.
  if (error || !inspection || !inspection.urgency) {
    return null;
  }

  // Only DUE inspections are interactive: tapping one opens the full
  // Inspection Details view. UPCOMING cards are informational only, so we
  // leave them non-clickable (no handler, no pointer cursor, no chevron).
  const isDue = inspection.urgency === "DUE";

  const handleClick = () => {
    navigate(`/configurationDrivenInspections/scheduled/${inspection.austinInspectionId}/checkpoints`, {
      state: {
        austinInspectionId: inspection.austinInspectionId,
        name: inspection.name,
        siteCode,
      } as AustinInspectionDetailsState,
    });
  };

  return (
    <div
      className="home-card"
      onClick={isDue ? handleClick : undefined}
      role={isDue ? "button" : undefined}
      style={{ cursor: isDue ? "pointer" : "default" }}
    >
      <Row alignmentVertical="center" spacing="400" spacingInset="400" alignmentHorizontal="justify">
        <Row className=" home-workflow-icon-bg" spacingInset="400">
          <Icon tokens={clipboardTokens} color="success" />
        </Row>
        <Column spacing="100" width="fill">
          <Text type="h200">{inspection.name}</Text>
          <Text type="b200" color="secondary">{DESCRIPTION}</Text>
        </Column>
        {isDue && <Icon tokens={chevronRightSmallTokens} />}
      </Row>

      <div className="home-card-divider">
        <Divider />
      </div>

      <Row alignmentVertical="center" spacing="300" spacingInset="300 400 400">
        <Tag type={getUrgencyTagType(inspection.urgency)}>
          {getUrgencyLabel(inspection.urgency)}
        </Tag>
        <Text type="b100" color="secondary">{buildDateLabel(inspection)}</Text>
      </Row>
    </div>
  );
};

export default AustinInspectionCard;
